import {
  type InfiniteData,
  isCancelledError,
  type QueryClient,
  type UseQueryResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IS_MIX_CATALOG } from "@/config/browserBoard";
import {
  type Catalog,
  INFINITE_CATALOG_QUERY_KEY_PREFIX,
  type ThreadSummary,
} from "@/entities/thread";
import {
  createCatalogAutoReloadQueryBehavior,
  shouldRefreshCatalogOnlineUsers,
} from "@/features/catalog/hooks/catalogAutoReload";
import {
  getBoardCatalogPath,
  getCatalogMixPath,
  getCatalogPath,
} from "@/features/catalog/hooks/catalogPath";
import {
  type CatalogResponse,
  mergeCatalogPages,
  mergeViewerShadowThreads,
  normalizeCatalogResponse,
} from "@/features/catalog/hooks/catalogResponse";
import {
  CATALOG_PAGE_LIMIT_MAX,
  type SortType,
  useCatalogStore,
} from "@/features/catalog/stores/catalogStore";
import {
  getMixCatalogPagesPerRound,
  type MixMergeInput,
  mergeMixCatalogRound,
  planMixFetch,
  sliceMixCatalogRound,
} from "@/features/catalog/utils/mixMerge";
import { useMixCandidateSlugs } from "@/features/settings/hooks/useMixCandidateSlugs";
import {
  type MixSelection,
  resolveMixSelection,
  useMixStore,
} from "@/features/settings/stores/mixStore";
import { apiGet, hasAimgToken } from "@/shared/api/client";
import { refreshOnlineUsers } from "@/shared/api/onlineUsers";
import { useAimgViewerContext } from "@/shared/api/useAimgViewerContext";
import {
  getDocumentVisibilityState,
  useDocumentVisibilityState,
} from "@/shared/hooks/useDocumentVisibilityState";
import {
  runAimogeDataHook,
  useAimogeCatalogSnapshot,
  useAimogeHookGeneration,
} from "@/shared/lib/aimoge";
import { getApiErrorMessage } from "@/shared/ui/feedback/apiErrorMessage";

export {
  CATALOG_AUTO_RELOAD_INTERVAL,
  getCatalogRefetchInterval,
} from "@/features/catalog/hooks/catalogAutoReload";

/** カタログの共有pageをこのフックで上書きしたい呼び出し元向け(例: リストモードはページングをモード別に独立させ、グリッド設定と切り離した固定件数を使う)。 */
export interface UseThreadsOverrides {
  page?: number;
  limit?: number;
}

/** カタログqueryを表示モードに応じて休止するための購読設定。 */
export interface UseThreadsOptions {
  enabled?: boolean;
}

export interface ThreadsQueryParams {
  page: number;
  limit: number;
}

export type CatalogFetcherOptions = {
  allowTokenIssue?: boolean;
  /** 他板の板スコープAPIを引くためのX-Board-Key上書き(総合カタログ用)。 */
  boardKey?: string;
  cache?: RequestCache;
  requiresToken?: boolean;
  signal?: AbortSignal;
};

export type CatalogFetcher = <T>(
  path: string,
  options?: CatalogFetcherOptions,
) => Promise<T>;

interface FetchCatalogInput {
  catalogPath: string;
  fetcher: CatalogFetcher;
  hasViewerToken: boolean;
  limit: number;
  page: number;
  signal?: AbortSignal;
}

export function getThreadsQueryKey(
  catalogPath: string,
  viewerContext: number,
): readonly ["threads", string, "viewer", number] {
  return ["threads", catalogPath, "viewer", viewerContext];
}

/** SP版の全取得ページを1つのcacheとして識別するquery keyを返す。 */
export function getInfiniteThreadsQueryKey(
  catalogPath: string,
  viewerContext: number,
): readonly ["threads", "infinite", string, "viewer", number] {
  return [
    ...INFINITE_CATALOG_QUERY_KEY_PREFIX,
    catalogPath,
    "viewer",
    viewerContext,
  ];
}

export async function fetchCatalogWithViewerShadowThreads({
  catalogPath,
  fetcher,
  hasViewerToken,
  limit,
  page,
  signal,
}: FetchCatalogInput): Promise<Catalog> {
  const catalogRequest = fetcher<CatalogResponse>(catalogPath, { signal });
  if (!hasViewerToken || page !== 1) {
    return normalizeCatalogResponse(await catalogRequest, limit);
  }

  const [response, shadowThreads] = await Promise.all([
    catalogRequest,
    fetcher<ThreadSummary[]>("/shadow-threads", {
      allowTokenIssue: false,
      cache: "no-store",
      requiresToken: true,
      signal,
    }).catch((error: unknown) => {
      if (error instanceof Error && error.name === "AbortError") {
        throw error;
      }
      return [];
    }),
  ]);
  return mergeViewerShadowThreads(
    normalizeCatalogResponse(response, limit),
    shadowThreads,
  );
}

export interface FetchMixCatalogInput {
  sort: SortType;
  page: number;
  limit: number;
  selection: MixSelection;
  fetcher: CatalogFetcher;
  hasViewerToken: boolean;
  signal?: AbortSignal;
}

export type FetchMixCatalogRoundInput = Omit<FetchMixCatalogInput, "page"> & {
  round: number;
};

interface MixCatalogGroups {
  sort: SortType;
  uncapped: MixMergeInput["uncapped"];
  extras: MixMergeInput["extras"];
}

interface MixCatalogRound extends Catalog {
  mixGroups: MixCatalogGroups;
  firstPageDisplayPlan?: MixFirstPageDisplayPlan;
}

interface MixFirstPageDisplayPlan {
  context: string;
  publicThreadIds: ReadonlySet<string>;
}

const MIX_CATALOG_CONCURRENCY = 4;

/**
 * 総合カタログのpublic取得roundを全群ぶん取得し、全体の並びを確定する。
 * 板数が表示枠を超える場合も全群を取得してから論理ページへ切り分ける。
 *
 * 全板に%上限を付けた構成では制御なし群が無くcatalog-mixを呼ばない
 * (空の`b=`はbackendが400で拒否する)。板数が表示枠を超えるときは全群を
 * 固定1件ずつ取得するroundを複数の表示ページへ分割して巡回する。
 */
export async function fetchMixCatalogRound(
  input: FetchMixCatalogRoundInput,
): Promise<MixCatalogRound> {
  const { uncappedLimit, extras: plans } = planMixFetch(
    input.selection,
    input.limit,
  );
  const requests: Array<() => Promise<Catalog | null>> = [];
  if (uncappedLimit !== null) {
    requests.push(() =>
      input
        .fetcher<CatalogResponse>(
          getCatalogMixPath(
            input.selection.uncappedSlugs,
            input.sort,
            input.round,
            uncappedLimit,
          ),
          { signal: input.signal },
        )
        .then((response) => normalizeCatalogResponse(response, uncappedLimit)),
    );
  }
  for (const plan of plans) {
    requests.push(
      (): Promise<Catalog | null> =>
        input
          .fetcher<CatalogResponse>(
            getBoardCatalogPath(plan.slug, input.sort, input.round, plan.limit),
            // 実backendはpath slugで板を解決しheaderを読まないが、boardKeyを
            // 対象板へ揃える規約(mix-catalog.md)としてE2Eモックが検査している
            { signal: input.signal, boardKey: plan.slug },
          )
          .then((response) => normalizeCatalogResponse(response, plan.limit))
          .catch((error: unknown): null => {
            if (error instanceof Error && error.name === "AbortError") {
              throw error;
            }
            console.warn(
              `[mix] ${plan.slug}板のカタログ取得に失敗しました`,
              error,
            );
            return null;
          }),
    );
  }
  const catalogs: Array<Catalog | null> = new Array(requests.length);
  let nextRequest = 0;
  let stopped = false;
  // A viewer shadow request runs beside this round on page one, so leave one
  // request slot available whenever that request can be enabled.
  const concurrency =
    input.hasViewerToken && input.selection.uncappedSlugs.length > 0
      ? MIX_CATALOG_CONCURRENCY - 1
      : MIX_CATALOG_CONCURRENCY;
  await Promise.all(
    Array.from({ length: Math.min(concurrency, requests.length) }, async () => {
      while (!stopped && nextRequest < requests.length) {
        try {
          input.signal?.throwIfAborted();
          const index = nextRequest++;
          catalogs[index] = (await requests[index]?.()) ?? null;
        } catch (error: unknown) {
          stopped = true;
          throw error;
        }
      }
    }),
  );
  const uncapped = uncappedLimit === null ? null : (catalogs[0] ?? null);
  const extraOffset = uncappedLimit === null ? 0 : 1;
  const mixGroups: MixCatalogGroups = {
    sort: input.sort,
    uncapped:
      uncapped === null
        ? null
        : { slugs: input.selection.uncappedSlugs, catalog: uncapped },
    extras: plans.map((plan, index) => ({
      slug: plan.slug,
      catalog: catalogs[index + extraOffset] ?? null,
    })),
  };
  const merged = mergeMixCatalogRound({
    ...mixGroups,
    pageLimit: input.limit,
    page: input.round,
  });
  return {
    ...merged,
    pagination: {
      ...merged.pagination,
      page: input.round,
      hasNextPage: catalogs.some((catalog) => catalog?.pagination.hasNextPage),
    },
    mixGroups,
  };
}

/** Shadowを制御なし群へ加えてから、round全体をsort/interleaveし直す。 */
function mergeShadowThreadsIntoMixRound(
  catalog: Catalog,
  shadowThreads: ThreadSummary[],
): Catalog {
  const { mixGroups } = catalog as MixCatalogRound;
  if (!mixGroups?.uncapped || shadowThreads.length === 0) return catalog;

  const anchorSlug = mixGroups.uncapped.slugs[0] ?? "";
  const shadowForMix = shadowThreads.map((thread) => ({
    ...thread,
    b: thread.b ?? anchorSlug,
  }));
  const uncappedCatalog = mergeViewerShadowThreads(
    mixGroups.uncapped.catalog,
    shadowForMix,
  );
  return mergeMixCatalogRound({
    ...mixGroups,
    pageLimit: catalog.pagination.limit,
    page: catalog.pagination.page,
    uncapped: { ...mixGroups.uncapped, catalog: uncappedCatalog },
  });
}

function stripMixCatalogGroups(catalog: Catalog): Catalog {
  const visibleCatalog = { ...catalog } as Catalog & Partial<MixCatalogRound>;
  delete visibleCatalog.mixGroups;
  delete visibleCatalog.firstPageDisplayPlan;
  return visibleCatalog;
}

async function fetchMixShadowThreads(
  fetcher: CatalogFetcher,
  signal?: AbortSignal,
): Promise<ThreadSummary[]> {
  try {
    return await fetcher<ThreadSummary[]>("/shadow-threads", {
      allowTokenIssue: false,
      cache: "no-store",
      requiresToken: true,
      signal,
    });
  } catch (error: unknown) {
    if (error instanceof Error && error.name === "AbortError") throw error;
    return [];
  }
}

/** 論理表示ページ用の直接取得adapter。query hooksはroundをcacheして再利用する。 */
export async function fetchMixCatalog(
  input: FetchMixCatalogInput,
): Promise<Catalog> {
  const pagesPerRound = getMixCatalogPagesPerRound(
    input.selection,
    input.limit,
  );
  const round = Math.floor((input.page - 1) / pagesPerRound) + 1;
  const pageInRound = (input.page - 1) % pagesPerRound;
  const roundCatalog = await fetchMixCatalogRound({ ...input, round });
  const publicCatalog = sliceMixCatalogRound(
    roundCatalog,
    pageInRound * input.limit,
    input.limit,
    input.page,
  );
  let displayRound: Catalog = roundCatalog;
  if (
    input.page === 1 &&
    input.hasViewerToken &&
    input.selection.uncappedSlugs.length > 0
  ) {
    const shadowThreads = await fetchMixShadowThreads(
      input.fetcher,
      input.signal,
    );
    displayRound = mergeShadowThreadsIntoMixRound(roundCatalog, shadowThreads);
  }
  const displayCatalog = sliceMixCatalogRound(
    displayRound,
    pageInRound * input.limit,
    input.limit,
    input.page,
  );
  return stripMixCatalogGroups({
    ...displayCatalog,
    pagination: publicCatalog.pagination,
    threads: displayCatalog.threads.slice(0, input.limit),
  });
}

/** 総合カタログのquery key用path。板構成と取り分の変更で自然に再取得させる。 */
export function getMixCatalogKeyPath(
  sort: SortType,
  round: number,
  limit: number,
  selection: MixSelection,
): string {
  const uncapped = selection.uncappedSlugs.join("+");
  const extras = selection.extras
    .map((extra) => `${extra.slug}.${extra.pct}`)
    .join("+");
  return `mix:${uncapped}?sort=${sort}&round=${round}&limit=${limit}&extras=${extras}`;
}

function getMixPagingContext(
  sort: SortType,
  limit: number,
  selection: MixSelection,
  boardRevision: number,
  viewerContext: number,
): string {
  return `${getMixCatalogKeyPath(sort, 1, limit, selection)}&revision=${boardRevision}\0${viewerContext}`;
}

function getDisplayedPublicThreadIds(
  displayedThreads: readonly ThreadSummary[],
  publicThreads: readonly ThreadSummary[],
): Set<string> {
  const publicIds = new Set(publicThreads.map((thread) => thread.id));
  return new Set(
    displayedThreads
      .filter((thread) => publicIds.has(thread.id))
      .map((thread) => thread.id),
  );
}

function getRemainingPublicThreads(
  publicThreads: readonly ThreadSummary[],
  displayedPublicThreadIds: ReadonlySet<string>,
): ThreadSummary[] {
  return publicThreads.filter(
    (thread) => !displayedPublicThreadIds.has(thread.id),
  );
}

function areMixFirstPageDisplayPlansEqual(
  current: MixFirstPageDisplayPlan | undefined,
  next: MixFirstPageDisplayPlan,
): boolean {
  if (
    !current ||
    current.context !== next.context ||
    current.publicThreadIds.size !== next.publicThreadIds.size
  ) {
    return false;
  }
  return [...next.publicThreadIds].every((id) =>
    current.publicThreadIds.has(id),
  );
}

interface MixCatalogPagePosition {
  round: number;
  pageInRound: number;
  exhausted: boolean;
}

/** 取得済みroundの実件数から論理ページのround/offsetを解決する。 */
function resolveMixCatalogPagePosition(
  queryClient: QueryClient,
  input: {
    boardRevision: number;
    limit: number;
    page: number;
    selection: MixSelection;
    sort: SortType;
    viewerContext: number;
    firstPagePublicIds?: ReadonlySet<string>;
  },
): MixCatalogPagePosition {
  if (getMixCatalogPagesPerRound(input.selection, input.limit) === 1) {
    return { round: input.page, pageInRound: 1, exhausted: false };
  }

  let round = 1;
  let firstLogicalPage = 1;
  while (round <= input.page) {
    const queryPath = `${getMixCatalogKeyPath(
      input.sort,
      round,
      input.limit,
      input.selection,
    )}&revision=${input.boardRevision}`;
    const cachedRound = queryClient.getQueryData<Catalog>(
      getThreadsQueryKey(queryPath, input.viewerContext),
    );
    if (!cachedRound) {
      return {
        round,
        pageInRound: input.page - firstLogicalPage + 1,
        exhausted: false,
      };
    }
    const pagesInRound =
      round === 1 && input.firstPagePublicIds
        ? 1 +
          Math.ceil(
            getRemainingPublicThreads(
              cachedRound.threads,
              input.firstPagePublicIds,
            ).length / input.limit,
          )
        : Math.max(1, Math.ceil(cachedRound.threads.length / input.limit));
    if (input.page < firstLogicalPage + pagesInRound) {
      return {
        round,
        pageInRound: input.page - firstLogicalPage + 1,
        exhausted: false,
      };
    }
    if (!cachedRound.pagination.hasNextPage) {
      return {
        round,
        pageInRound: input.page - firstLogicalPage + 1,
        exhausted: true,
      };
    }
    firstLogicalPage += pagesInRound;
    round += 1;
  }
  return { round, pageInRound: 1, exhausted: false };
}

/** storeのpage/columns/rowsとoverridesから実際に使うpage/limitを決定する純粋関数(テスト容易性のため分離)。 */
export function resolveThreadsQueryParams(
  store: { page: number; columns: number; rows: number },
  overrides?: UseThreadsOverrides,
): ThreadsQueryParams {
  return {
    page: overrides?.page ?? store.page,
    limit:
      overrides?.limit ??
      Math.max(1, Math.min(CATALOG_PAGE_LIMIT_MAX, store.columns * store.rows)),
  };
}

/** 拡張フックをカタログと各スレッドへ適用する安定した変換関数を返す。 */
function useTransformCatalog(): (catalog: Catalog) => Catalog {
  const aimogeGeneration = useAimogeHookGeneration();
  return useCallback(
    (catalog: Catalog): Catalog => {
      void aimogeGeneration;
      const transformedCatalog = runAimogeDataHook("data:catalog", catalog);
      return {
        ...transformedCatalog,
        threads: transformedCatalog.threads.map((thread) =>
          runAimogeDataHook("data:thread", thread),
        ),
      };
    },
    [aimogeGeneration],
  );
}

export function useThreads(
  overrides?: UseThreadsOverrides,
  options: UseThreadsOptions = {},
): UseQueryResult<Catalog> {
  const { currentSort, page, columns, rows, autoReload } = useCatalogStore();
  const mixBoardSettings = useMixStore((state) => state.boardSettings);
  const mixSlugs = useMixCandidateSlugs();
  const boardRevision = useMixStore((state) => state.boardRevision);
  const { page: resolvedPage, limit } = resolveThreadsQueryParams(
    { page, columns, rows },
    overrides,
  );
  const mixSelection = IS_MIX_CATALOG
    ? (resolveMixSelection(mixSlugs, mixBoardSettings) ?? {
        uncappedSlugs: [],
        extras: [],
      })
    : null;
  const queryClient = useQueryClient();
  const viewerContext = useAimgViewerContext();
  const visibilityState = useDocumentVisibilityState();
  const lastFetchContextRef = useRef<string | null>(null);
  const mixPagesPerRound = mixSelection
    ? getMixCatalogPagesPerRound(mixSelection, limit)
    : 1;
  const mixPagingContext = mixSelection
    ? getMixPagingContext(
        currentSort,
        limit,
        mixSelection,
        boardRevision,
        viewerContext,
      )
    : null;
  const firstRoundQueryPath = mixSelection
    ? `${getMixCatalogKeyPath(currentSort, 1, limit, mixSelection)}&revision=${boardRevision}`
    : null;
  const firstRoundQueryKey = firstRoundQueryPath
    ? getThreadsQueryKey(firstRoundQueryPath, viewerContext)
    : null;
  const cachedFirstPageDisplayPlan = firstRoundQueryKey
    ? queryClient.getQueryData<MixCatalogRound>(firstRoundQueryKey)
        ?.firstPageDisplayPlan
    : undefined;
  const mixFirstPageDisplayPlan =
    cachedFirstPageDisplayPlan?.context === mixPagingContext
      ? cachedFirstPageDisplayPlan
      : null;
  const mixPagePosition = mixSelection
    ? resolveMixCatalogPagePosition(queryClient, {
        boardRevision,
        limit,
        page: resolvedPage,
        selection: mixSelection,
        sort: currentSort,
        viewerContext,
        firstPagePublicIds: mixFirstPageDisplayPlan?.publicThreadIds,
      })
    : null;
  const catalogPath = mixSelection
    ? getMixCatalogKeyPath(
        currentSort,
        mixPagePosition?.round ?? 1,
        limit,
        mixSelection,
      )
    : getCatalogPath(currentSort, resolvedPage, limit);
  const publicQueryPath = IS_MIX_CATALOG
    ? `${catalogPath}&revision=${boardRevision}`
    : catalogPath;
  const fetchContext = `${publicQueryPath}\0${viewerContext}`;
  const transformCatalog = useTransformCatalog();
  const hasViewerToken = hasAimgToken();
  const mixShadowEnabled =
    options.enabled !== false &&
    mixSelection !== null &&
    resolvedPage === 1 &&
    hasViewerToken &&
    mixSelection.uncappedSlugs.length > 0;
  const shadowQueryPath = mixSelection
    ? `${getMixCatalogKeyPath(currentSort, 1, limit, mixSelection)}&revision=${boardRevision}&shadow`
    : "mix:shadow-disabled";
  const shadowQuery = useQuery<ThreadSummary[], Error>({
    queryKey: getThreadsQueryKey(shadowQueryPath, viewerContext),
    queryFn: ({ signal }: { signal: AbortSignal }): Promise<ThreadSummary[]> =>
      fetchMixShadowThreads(apiGet, signal),
    ...createCatalogAutoReloadQueryBehavior(autoReload, () => visibilityState),
    enabled: mixShadowEnabled,
  });
  const selectCatalog = useCallback(
    (catalog: Catalog): Catalog => {
      if (!mixSelection || !mixPagePosition) return transformCatalog(catalog);
      const publicSelected = sliceMixCatalogRound(
        catalog,
        (mixPagePosition.pageInRound - 1) * limit,
        limit,
        resolvedPage,
      );
      let selected: Catalog;
      if (resolvedPage === 1) {
        const displayRound = shadowQuery.data
          ? mergeShadowThreadsIntoMixRound(catalog, shadowQuery.data)
          : catalog;
        selected = sliceMixCatalogRound(displayRound, 0, limit, resolvedPage);
        selected = {
          ...selected,
          pagination:
            mixPagesPerRound > 1
              ? {
                  page: resolvedPage,
                  limit,
                  hasNextPage:
                    getRemainingPublicThreads(
                      catalog.threads,
                      getDisplayedPublicThreadIds(
                        selected.threads,
                        catalog.threads,
                      ),
                    ).length > 0 || catalog.pagination.hasNextPage,
                }
              : publicSelected.pagination,
        };
      } else if (
        mixPagesPerRound > 1 &&
        mixPagePosition.round === 1 &&
        mixFirstPageDisplayPlan?.context === mixPagingContext
      ) {
        const remainingPublic = getRemainingPublicThreads(
          catalog.threads,
          mixFirstPageDisplayPlan.publicThreadIds,
        );
        selected = sliceMixCatalogRound(
          { ...catalog, threads: remainingPublic },
          (resolvedPage - 2) * limit,
          limit,
          resolvedPage,
        );
      } else {
        selected = publicSelected;
      }
      return transformCatalog(stripMixCatalogGroups(selected));
    },
    [
      limit,
      mixFirstPageDisplayPlan,
      mixPagesPerRound,
      mixPagingContext,
      mixPagePosition,
      mixSelection,
      resolvedPage,
      shadowQuery.data,
      transformCatalog,
    ],
  );

  const query = useQuery({
    queryKey: getThreadsQueryKey(publicQueryPath, viewerContext),
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const isInitialFetch = lastFetchContextRef.current !== fetchContext;
      const firstPagePlanContextAtStart = mixPagingContext;
      const firstRoundQueryKeyAtStart =
        mixSelection && mixPagePosition?.round === 1 && firstRoundQueryKey
          ? firstRoundQueryKey
          : null;
      const catalogPromise = mixSelection
        ? fetchMixCatalogRound({
            sort: currentSort,
            round: mixPagePosition?.round ?? 1,
            limit,
            selection: mixSelection,
            fetcher: apiGet,
            hasViewerToken,
            signal,
          })
        : fetchCatalogWithViewerShadowThreads({
            catalogPath,
            fetcher: apiGet,
            hasViewerToken,
            limit,
            page: resolvedPage,
            signal,
          });
      if (
        (mixSelection === null ||
          mixSelection.uncappedSlugs.length + mixSelection.extras.length > 0) &&
        shouldRefreshCatalogOnlineUsers(
          autoReload,
          isInitialFetch,
          getDocumentVisibilityState(),
        )
      ) {
        void refreshOnlineUsers(queryClient);
      }
      const catalog = await catalogPromise;
      lastFetchContextRef.current = fetchContext;
      const latestFirstPageDisplayPlan = firstRoundQueryKeyAtStart
        ? queryClient.getQueryData<MixCatalogRound>(firstRoundQueryKeyAtStart)
            ?.firstPageDisplayPlan
        : undefined;
      if (latestFirstPageDisplayPlan?.context === firstPagePlanContextAtStart) {
        return {
          ...(catalog as MixCatalogRound),
          firstPageDisplayPlan: latestFirstPageDisplayPlan,
        };
      }
      return catalog;
    },
    select: selectCatalog,
    ...createCatalogAutoReloadQueryBehavior(autoReload, () => visibilityState),
    enabled: options.enabled ?? true,
  });
  useEffect(() => {
    if (
      mixPagesPerRound <= 1 ||
      resolvedPage !== 1 ||
      mixPagingContext === null ||
      query.status !== "success" ||
      !query.data
    ) {
      return;
    }
    const cachedRound = queryClient.getQueryData<MixCatalogRound>(
      getThreadsQueryKey(publicQueryPath, viewerContext),
    );
    if (!cachedRound) return;
    const nextDisplayPlan: MixFirstPageDisplayPlan = {
      context: mixPagingContext,
      publicThreadIds: getDisplayedPublicThreadIds(
        query.data.threads,
        cachedRound.threads,
      ),
    };
    if (
      areMixFirstPageDisplayPlansEqual(
        cachedRound.firstPageDisplayPlan,
        nextDisplayPlan,
      )
    ) {
      return;
    }
    const firstRoundState = queryClient.getQueryState(
      getThreadsQueryKey(publicQueryPath, viewerContext),
    );
    queryClient.setQueryData<MixCatalogRound>(
      getThreadsQueryKey(publicQueryPath, viewerContext),
      (current) =>
        current
          ? { ...current, firstPageDisplayPlan: nextDisplayPlan }
          : current,
      firstRoundState
        ? { updatedAt: firstRoundState.dataUpdatedAt }
        : undefined,
    );
  }, [
    mixPagesPerRound,
    mixPagingContext,
    publicQueryPath,
    query.data,
    query.status,
    queryClient,
    resolvedPage,
    viewerContext,
  ]);
  const refetch: UseQueryResult<Catalog>["refetch"] = useCallback(
    (refetchOptions) => {
      const publicRefetch = query.refetch(refetchOptions);
      if (!mixShadowEnabled) return publicRefetch;
      const shadowRefetch = shadowQuery.refetch(refetchOptions);
      return Promise.all([publicRefetch, shadowRefetch]).then(
        ([publicResult]) => publicResult,
      );
    },
    [mixShadowEnabled, query.refetch, shadowQuery.refetch],
  );

  // 拡張向けsnapshot。selectを通した後の1本だけを公開するため、mixでは
  // 混合後の結果、個別板ではその板の結果になる(板ごとの内訳は出さない)。
  useAimogeCatalogSnapshot(options.enabled === false ? undefined : query.data);

  return { ...query, refetch };
}

export type InfiniteThreadsQueryResult = UseQueryResult<Catalog> & {
  fetchNextPage: () => Promise<void>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  nextPageError: string | null;
};

interface CachedMixRound {
  catalog: Catalog;
  round: number;
  remainingPublicThreads: ThreadSummary[];
}

interface InfiniteCatalogData extends InfiniteData<Catalog, number> {
  mixRound?: CachedMixRound;
}

/** SP版カタログを1ページ目から順に追記取得する。 */
export function useInfiniteThreads(
  options: UseThreadsOptions = {},
): InfiniteThreadsQueryResult {
  const { currentSort, columns, rows, autoReload } = useCatalogStore();
  const mixBoardSettings = useMixStore((state) => state.boardSettings);
  const mixSlugs = useMixCandidateSlugs();
  const boardRevision = useMixStore((state) => state.boardRevision);
  const limit = resolveThreadsQueryParams({
    page: 1,
    columns,
    rows,
  }).limit;
  const mixSelection = IS_MIX_CATALOG
    ? (resolveMixSelection(mixSlugs, mixBoardSettings) ?? {
        uncappedSlugs: [],
        extras: [],
      })
    : null;
  const mixPagesPerRound = mixSelection
    ? getMixCatalogPagesPerRound(mixSelection, limit)
    : 1;
  const catalogKeyPath = mixSelection
    ? getMixCatalogKeyPath(currentSort, 1, limit, mixSelection)
    : getCatalogPath(currentSort, 1, limit);
  const queryClient = useQueryClient();
  const viewerContext = useAimgViewerContext();
  const visibilityState = useDocumentVisibilityState();
  const lastFetchContextRef = useRef<string | null>(null);
  const isFetchingNextPageRef = useRef(false);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const [nextPageError, setNextPageError] = useState<string | null>(null);
  const queryGenerationRef = useRef(0);
  const transformCatalog = useTransformCatalog();
  const mergePages = useCallback(
    (data: InfiniteCatalogData): Catalog =>
      transformCatalog(mergeCatalogPages(data.pages)),
    [transformCatalog],
  );

  const queryKey = useMemo(
    () =>
      getInfiniteThreadsQueryKey(
        IS_MIX_CATALOG
          ? `${catalogKeyPath}&revision=${boardRevision}`
          : catalogKeyPath,
        viewerContext,
      ),
    [catalogKeyPath, viewerContext, boardRevision],
  );
  const queryIdentity = JSON.stringify(queryKey);
  const queryIdentityRef = useRef(queryIdentity);
  queryIdentityRef.current = queryIdentity;
  const fetchPage = useCallback(
    (page: number, signal?: AbortSignal): Promise<Catalog> =>
      mixSelection
        ? fetchMixCatalog({
            sort: currentSort,
            page,
            limit,
            selection: mixSelection,
            fetcher: apiGet,
            hasViewerToken: hasAimgToken(),
            signal,
          })
        : fetchCatalogWithViewerShadowThreads({
            catalogPath: getCatalogPath(currentSort, page, limit),
            fetcher: apiGet,
            hasViewerToken: hasAimgToken(),
            limit,
            page,
            signal,
          }),
    [currentSort, limit, mixSelection],
  );

  const query = useQuery<
    InfiniteCatalogData,
    Error,
    Catalog,
    ReturnType<typeof getInfiniteThreadsQueryKey>
  >({
    queryKey,
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      await queryClient.cancelQueries({ queryKey: [...queryKey, "page"] });
      queryGenerationRef.current += 1;
      const fetchContext = `${catalogKeyPath}&revision=${boardRevision}\0${viewerContext}`;
      const isInitialFetch = lastFetchContextRef.current !== fetchContext;
      let catalog: Catalog;
      let mixRound: CachedMixRound | undefined;
      if (mixSelection) {
        const roundPromise = fetchMixCatalogRound({
          sort: currentSort,
          round: 1,
          limit,
          selection: mixSelection,
          fetcher: apiGet,
          hasViewerToken: hasAimgToken(),
          signal,
        });
        const shadowPromise =
          hasAimgToken() && mixSelection.uncappedSlugs.length > 0
            ? fetchMixShadowThreads(apiGet, signal)
            : Promise.resolve([] as ThreadSummary[]);
        const [publicRound, shadowThreads] = await Promise.all([
          roundPromise,
          shadowPromise,
        ]);
        const displayRound = mergeShadowThreadsIntoMixRound(
          publicRound,
          shadowThreads,
        );
        const displayFirstPage = sliceMixCatalogRound(
          displayRound,
          0,
          limit,
          1,
        );
        const displayedPublicIds = getDisplayedPublicThreadIds(
          displayFirstPage.threads,
          publicRound.threads,
        );
        const remainingPublicThreads = getRemainingPublicThreads(
          publicRound.threads,
          displayedPublicIds,
        );
        catalog = stripMixCatalogGroups({
          ...displayFirstPage,
          pagination:
            mixPagesPerRound > 1
              ? {
                  page: 1,
                  limit,
                  hasNextPage:
                    remainingPublicThreads.length > 0 ||
                    publicRound.pagination.hasNextPage,
                }
              : publicRound.pagination,
          threads: displayFirstPage.threads.slice(0, limit),
        });
        mixRound = {
          catalog: publicRound,
          round: 1,
          remainingPublicThreads:
            mixPagesPerRound > 1 ? remainingPublicThreads : [],
        };
      } else {
        catalog = await fetchPage(1, signal);
      }
      if (
        (mixSelection === null ||
          mixSelection.uncappedSlugs.length + mixSelection.extras.length > 0) &&
        shouldRefreshCatalogOnlineUsers(
          autoReload,
          isInitialFetch,
          getDocumentVisibilityState(),
        )
      ) {
        void refreshOnlineUsers(queryClient);
      }
      lastFetchContextRef.current = fetchContext;
      return {
        pages: [catalog],
        pageParams: [1],
        ...(mixRound ? { mixRound } : {}),
      };
    },
    select: mergePages,
    ...createCatalogAutoReloadQueryBehavior(autoReload, () => visibilityState),
    enabled: options.enabled ?? true,
  });

  useEffect(
    () => (): void => {
      void queryClient.cancelQueries({ queryKey });
    },
    [queryClient, queryKey],
  );

  const fetchNextPage = useCallback(async (): Promise<void> => {
    if (isFetchingNextPageRef.current || query.isFetching) return;
    const current = queryClient.getQueryData<InfiniteCatalogData>(queryKey);
    const lastPage = current?.pages.at(-1);
    if (!current || !lastPage?.pagination.hasNextPage) return;

    const nextPage = lastPage.pagination.page + 1;
    const identityAtStart = queryIdentity;
    const generationAtStart = queryGenerationRef.current;
    isFetchingNextPageRef.current = true;
    setIsFetchingNextPage(true);
    setNextPageError(null);
    try {
      const pageQueryKey = [...queryKey, "page", nextPage] as const;
      let nextCatalog: Catalog;
      let nextMixRound = current.mixRound;
      try {
        if (mixSelection && current.mixRound) {
          const { catalog, round, remainingPublicThreads } = current.mixRound;
          if (mixPagesPerRound > 1 && remainingPublicThreads.length > 0) {
            nextCatalog = sliceMixCatalogRound(
              { ...catalog, threads: remainingPublicThreads },
              0,
              limit,
              nextPage,
            );
            nextMixRound = {
              ...current.mixRound,
              remainingPublicThreads: remainingPublicThreads.slice(limit),
            };
          } else if (catalog.pagination.hasNextPage) {
            const nextRound = await queryClient.fetchQuery({
              queryKey: pageQueryKey,
              queryFn: ({ signal }: { signal: AbortSignal }) =>
                fetchMixCatalogRound({
                  sort: currentSort,
                  round: round + 1,
                  limit,
                  selection: mixSelection,
                  fetcher: apiGet,
                  hasViewerToken: hasAimgToken(),
                  signal,
                }),
              staleTime: 0,
              gcTime: 0,
            });
            nextMixRound = {
              catalog: nextRound,
              round: round + 1,
              remainingPublicThreads:
                mixPagesPerRound > 1 ? nextRound.threads.slice(limit) : [],
            };
            nextCatalog = sliceMixCatalogRound(nextRound, 0, limit, nextPage);
          } else {
            return;
          }
        } else if (mixSelection) {
          const nextRound = await queryClient.fetchQuery({
            queryKey: pageQueryKey,
            queryFn: ({ signal }: { signal: AbortSignal }) =>
              fetchMixCatalogRound({
                sort: currentSort,
                round: 1,
                limit,
                selection: mixSelection,
                fetcher: apiGet,
                hasViewerToken: hasAimgToken(),
                signal,
              }),
            staleTime: 0,
            gcTime: 0,
          });
          const remainingPublicThreads =
            mixPagesPerRound > 1
              ? getRemainingPublicThreads(
                  nextRound.threads,
                  new Set(
                    current.pages.flatMap((page) =>
                      Array.from(
                        getDisplayedPublicThreadIds(
                          page.threads,
                          nextRound.threads,
                        ),
                      ),
                    ),
                  ),
                )
              : [];
          nextMixRound = {
            catalog: nextRound,
            round: 1,
            remainingPublicThreads:
              mixPagesPerRound > 1 ? remainingPublicThreads.slice(limit) : [],
          };
          nextCatalog = sliceMixCatalogRound(
            mixPagesPerRound > 1
              ? { ...nextRound, threads: remainingPublicThreads }
              : nextRound,
            mixPagesPerRound > 1 ? 0 : (nextPage - 1) * limit,
            limit,
            nextPage,
          );
        } else {
          nextCatalog = await queryClient.fetchQuery({
            queryKey: pageQueryKey,
            queryFn: ({ signal }: { signal: AbortSignal }): Promise<Catalog> =>
              fetchPage(nextPage, signal),
            staleTime: 0,
            gcTime: 0,
          });
        }
      } finally {
        queryClient.removeQueries({ queryKey: pageQueryKey, exact: true });
      }
      if (
        queryIdentityRef.current !== identityAtStart ||
        queryGenerationRef.current !== generationAtStart
      ) {
        return;
      }
      queryClient.setQueryData<InfiniteCatalogData>(queryKey, (latest) => {
        const latestLastPage = latest?.pages.at(-1);
        if (
          !latest ||
          !latestLastPage?.pagination.hasNextPage ||
          latestLastPage.pagination.page + 1 !== nextPage
        ) {
          return latest;
        }
        return {
          pages: [...latest.pages, nextCatalog],
          pageParams: [...latest.pageParams, nextPage],
          ...(nextMixRound ? { mixRound: nextMixRound } : {}),
        };
      });
    } catch (error) {
      if (!isCancelledError(error)) {
        setNextPageError(
          getApiErrorMessage(error, "カタログの続きを読み込めませんでした"),
        );
      }
    } finally {
      isFetchingNextPageRef.current = false;
      setIsFetchingNextPage(false);
    }
  }, [
    currentSort,
    fetchPage,
    limit,
    mixPagesPerRound,
    mixSelection,
    query.isFetching,
    queryIdentity,
    queryClient,
    queryKey,
  ]);

  useAimogeCatalogSnapshot(options.enabled === false ? undefined : query.data);

  return {
    ...query,
    fetchNextPage,
    hasNextPage: query.data?.pagination.hasNextPage ?? false,
    isFetching: query.isFetching || isFetchingNextPage,
    isFetchingNextPage,
    nextPageError,
  };
}
